import { useState, useMemo } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { useDebounce } from "@/MyCode/hooks/useDebounce"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"

export function RentCalculator() {
    const [preTaxYearlyIncome, setPreTaxYearlyIncome] = useState(100000)
    const [monthlyDebtPayback, setMonthlyDebtPayback] = useState(0)

    // Debounced values
    const debouncedPreTaxYearlyIncome = useDebounce(preTaxYearlyIncome, 500)
    const debouncedMonthlyDebtPayback = useDebounce(monthlyDebtPayback, 500)

    // Calculate monthly income
    const monthlyIncome = useMemo(() => {
        return debouncedPreTaxYearlyIncome / 12
    }, [debouncedPreTaxYearlyIncome])

    // Calculate available income after debt
    const availableIncome = useMemo(() => {
        return Math.max(0, monthlyIncome - debouncedMonthlyDebtPayback)
    }, [monthlyIncome, debouncedMonthlyDebtPayback])

    // Calculate rent affordability
    const rentCalculation = useMemo(() => {
        if (debouncedPreTaxYearlyIncome <= 0) {
            return {
                recommendedRent: 0,
                maxRent: 0,
                safeThreshold: 0,
                acceptableThreshold: 0,
                aggressiveThreshold: 0,
            }
        }

        // Safe: 30% of available income
        const safeThreshold = availableIncome * 0.30
        // Acceptable: 40% of available income
        const acceptableThreshold = availableIncome * 0.40
        // Aggressive: 50% of available income
        const aggressiveThreshold = availableIncome * 0.50

        return {
            recommendedRent: safeThreshold,
            maxRent: aggressiveThreshold,
            safeThreshold,
            acceptableThreshold,
            aggressiveThreshold,
        }
    }, [availableIncome, debouncedPreTaxYearlyIncome])

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.preTaxYearlyIncome !== undefined) setPreTaxYearlyIncome(Number(data.preTaxYearlyIncome))
        if (data.monthlyDebtPayback !== undefined) setMonthlyDebtPayback(Number(data.monthlyDebtPayback))
    }

    const steps: Step[] = [
        {
            id: "income-and-debt",
            title: "Income & Debt",
            description: "Enter your pre-tax yearly income and monthly debt payments",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "preTaxYearlyIncome",
                    label: "Pre-tax yearly income",
                    placeholder: "100000",
                    required: true,
                    minValue: 0,
                    maxValue: 100000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    description: "before tax",
                    laravelValidationRules: {
                        preTaxYearlyIncome: 'required|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "monthlyDebtPayback",
                    label: "Monthly debt payback",
                    placeholder: "0",
                    required: false,
                    minValue: 0,
                    maxValue: 10000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 12 },
                    description: "car loans, student loans, credit cards, etc.",
                    laravelValidationRules: {
                        monthlyDebtPayback: 'nullable|numeric|min:0'
                    }
                },
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">How Much Rent Can I Afford?</h1>
                <p className="text-muted-foreground">
                    Calculate how much rent you can afford based on your income and debt obligations
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            preTaxYearlyIncome: preTaxYearlyIncome,
                            monthlyDebtPayback: monthlyDebtPayback,
                        }}
                        resetOnInitialValuesChange={false}
                    />
                </motion.div>

                {/* Right Side - Results */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-6"
                >
                    {/* Main Result */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h2 className="text-xl font-semibold mb-4">Result</h2>
                        <div className="space-y-4">
                            <div>
                                <p className="text-base text-muted-foreground mb-2">
                                    You can afford up to
                                </p>
                                <p className="text-3xl font-bold text-primary">
                                    $<AnimatedNumber value={rentCalculation.maxRent} decimals={0} />
                                </p>
                                <p className="text-base text-muted-foreground mt-1">
                                    per month on a rental payment.
                                </p>
                            </div>
                            <div className="pt-4 border-t border-border">
                                <p className="text-base text-muted-foreground mb-2">
                                    It is recommended to keep your rental payment below
                                </p>
                                <p className="text-2xl font-semibold text-primary">
                                    $<AnimatedNumber value={rentCalculation.recommendedRent} decimals={0} />
                                </p>
                                <p className="text-base text-muted-foreground mt-1">
                                    per month.
                                </p>
                            </div>
                            {rentCalculation.recommendedRent < 999 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                    className="mt-4 pt-4 border-t border-border"
                                >
                                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                        <p className="text-sm text-blue-900 dark:text-blue-100 leading-relaxed">
                                            <span className="font-semibold">We understand this can feel challenging.</span> Your financial situation is temporary, and there are resources available to help. Many people face similar circumstances, and you're taking the right step by planning ahead. Consider exploring housing assistance programs, roommates, or areas with lower cost of living. Remember, your worth isn't defined by your budget, and this is just one moment in your journey. You've got this.
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </div>




                </motion.div>
            </div>
            {/* Affordability Bar */}
            <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Affordability Range</h3>
                <div className="space-y-4">
                    {/* Bar visualization */}
                    <div className="relative w-full h-8 bg-secondary rounded-full overflow-hidden">
                        {/* Safe zone (green) */}
                        <div
                            className="absolute left-0 top-0 h-full bg-green-500"
                            style={{
                                width: `${(rentCalculation.safeThreshold / rentCalculation.aggressiveThreshold) * 100}%`
                            }}
                        />
                        {/* Acceptable zone (yellow) */}
                        <div
                            className="absolute h-full bg-yellow-500"
                            style={{
                                left: `${(rentCalculation.safeThreshold / rentCalculation.aggressiveThreshold) * 100}%`,
                                width: `${((rentCalculation.acceptableThreshold - rentCalculation.safeThreshold) / rentCalculation.aggressiveThreshold) * 100}%`
                            }}
                        />
                        {/* Aggressive zone (red) */}
                        <div
                            className="absolute h-full bg-red-500"
                            style={{
                                left: `${(rentCalculation.acceptableThreshold / rentCalculation.aggressiveThreshold) * 100}%`,
                                width: `${((rentCalculation.aggressiveThreshold - rentCalculation.acceptableThreshold) / rentCalculation.aggressiveThreshold) * 100}%`
                            }}
                        />
                    </div>

                    {/* Labels */}
                    <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-green-500"></div>
                            <span className="text-muted-foreground">Safe (≤30%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-yellow-500"></div>
                            <span className="text-muted-foreground">Acceptable (30-40%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-red-500"></div>
                            <span className="text-muted-foreground">Aggressive (40-50%)</span>
                        </div>
                    </div>

                    {/* Values */}
                    <div className="space-y-2 pt-2 border-t border-border">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Safe range:</span>
                            <span className="font-semibold text-green-600">
                                $0 - $<AnimatedNumber value={rentCalculation.safeThreshold} decimals={0} />
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Acceptable range:</span>
                            <span className="font-semibold text-yellow-600">
                                $<AnimatedNumber value={rentCalculation.safeThreshold} decimals={0} /> - $<AnimatedNumber value={rentCalculation.acceptableThreshold} decimals={0} />
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Aggressive range:</span>
                            <span className="font-semibold text-red-600">
                                $<AnimatedNumber value={rentCalculation.acceptableThreshold} decimals={0} /> - $<AnimatedNumber value={rentCalculation.aggressiveThreshold} decimals={0} />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {/* Income Breakdown */}
            <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Income Breakdown</h3>
                <div className="space-y-3">
                    <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Monthly income:</span>
                        <span className="text-sm font-semibold">
                            $<AnimatedNumber value={monthlyIncome} decimals={0} />
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Monthly debt payments:</span>
                        <span className="text-sm font-semibold">
                            $<AnimatedNumber value={debouncedMonthlyDebtPayback} decimals={0} />
                        </span>
                    </div>
                    <div className="pt-3 border-t border-border flex justify-between">
                        <span className="text-base font-semibold">Available for rent:</span>
                        <span className="text-base font-bold text-primary">
                            $<AnimatedNumber value={availableIncome} decimals={0} />
                        </span>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}

